import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import VirtualMachinesList from 'app/plugins/openstack/components/vms/VirtualMachinesList'
import { RootState, useAppSelector } from 'app/store'
import InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import DocumentMeta from 'core/components/DocumentMeta'
import { HeaderPrimaryActionPortal } from 'core/elements/header/portals'
import Tab from 'core/elements/tabs/Tab'
import Tabs from 'core/elements/tabs/Tabs'
import useListAction from 'core/hooks/useListAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { prop } from 'ramda'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import {
  listAllStatewiseVirtualMachinesLite,
  listSelfStatewiseVirtualMachinesLite,
  listStatewiseVirtualMachinesLite,
  listVirtualMachines,
} from './actions'
import CreateVirtualMachineModal from './CreateVirtualMachineModal'
import QuotaUsageData from './QuotaUsageData'
import {
  allStatewiseVirtualMachinesLiteSelector,
  selfStatewiseVirtualMachinesLiteSelector,
  statewiseVirtualMachinesLiteSelector,
} from './selectors'
import VMResourceUtilizationDashboard from './VMResourceUtilizationDashboard'

export enum IVirtualMachinesListPageTabs {
  VirtualMachinesList = 'list',
  ResourceUtilization = 'resource-utilization',
}

type ActionParams = InferActionParams<typeof listVirtualMachines>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {
  viewAllTenants?: boolean
}

export default function VirtualMachinesListPage() {
  const { history } = useReactRouter()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const userRole = session?.userDetails?.role
  const allTenants = session?.vmsAllTenants
  const allOwners = session?.vmsAllOwners

  const { reload: statewiseVMsReload, loading: statewiseVMsLoading } = useListAction(
    listStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
    },
  )
  const statewiseVMsData = useAppSelector(statewiseVirtualMachinesLiteSelector)

  const { reload: statewiseAllVMsReload, loading: statewiseAllVMsLoading } = useListAction(
    listAllStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
    },
  )
  const statewiseAllVMsData = useAppSelector(allStatewiseVirtualMachinesLiteSelector)

  const { reload: selfStatewiseVMsReload, loading: selfStatewiseVMsLoading } = useListAction(
    listSelfStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
    },
  )
  const selfStatewiseVMsData = useAppSelector(selfStatewiseVirtualMachinesLiteSelector)

  // Load statewise data based on user context
  useEffect(() => {
    if (allTenants) {
      if (
        statewiseAllVMsData.success === 0 &&
        statewiseAllVMsData.warning === 0 &&
        statewiseAllVMsData.error === 0
      ) {
        statewiseAllVMsReload()
      }
    } else if (userRole === 'member' && !allOwners) {
      if (
        selfStatewiseVMsData.success === 0 &&
        selfStatewiseVMsData.warning === 0 &&
        selfStatewiseVMsData.error === 0
      ) {
        selfStatewiseVMsReload()
      }
    } else {
      if (
        statewiseVMsData.success === 0 &&
        statewiseVMsData.warning === 0 &&
        statewiseVMsData.error === 0
      ) {
        statewiseVMsReload()
      }
    }
  }, [allTenants, userRole, allOwners])

  // Get statewise data based on user context
  const vmStateCount = useMemo(() => {
    if (allTenants) {
      return statewiseAllVMsData
    } else if (userRole === 'member' && !allOwners) {
      return selfStatewiseVMsData
    }
    return statewiseVMsData
  }, [statewiseVMsData, statewiseAllVMsData, selfStatewiseVMsData, allTenants, userRole])

  return (
    <>
      <DocumentMeta title="Virtual Machines" breadcrumbs />
      <HeaderPrimaryActionPortal>
        {!isReadonlyUser() && (
          <CreateButton onClick={() => history.push(routeManager.routes.openstack.deployVm.path())}>
            Deploy New VM
          </CreateButton>
        )}
      </HeaderPrimaryActionPortal>
      {/* {!allTenants && !!data?.length && <VmInfoCards vms={data} />}
      {allTenants && !!allData?.length && <VmInfoCards vms={allData} />} */}
      <QuotaUsageData
        vms={vmStateCount}
        loadingVMs={statewiseVMsLoading || statewiseAllVMsLoading || selfStatewiseVMsLoading}
      />
      <br />
      <Tabs route={routeManager.routes.openstack.vms} routeKey="vmstab">
        <Tab label="Virtual Machines" value={IVirtualMachinesListPageTabs.VirtualMachinesList}>
          <VirtualMachinesList />
        </Tab>
        <Tab
          label="Resource Utilization (All Tenants VMs)"
          value={IVirtualMachinesListPageTabs.ResourceUtilization}
          hidden={!isAdmin()}
        >
          <VMResourceUtilizationDashboard />
        </Tab>
      </Tabs>
    </>
  )
}
